import { TABLES, supabase } from "../lib/supabase";
import {
  blobToBase64,
  determinePdfTemplate,
  generateContractPdf,
} from "../lib/pdf/contractPdfGenerator";
import { retryContractEmail } from "../lib/email/resendClient";
import { getWorkerData } from "../utils/workerData";
import { downloadSignatureAsBase64 } from "../utils/signatureStorage";
import type { ContractPdfData, EmailAuditMetadata } from "../types/pdf";
import type { EmailMessageRow } from "./email-audit";

const MANAGEMENT_EMAIL = "verwaltung@teleconnect24.de";

interface AddressSnapshot {
  id: number | null;
  street: string;
  house_number: string;
  zip_code: string;
  city: string;
  country: string;
}

interface ContractRetryRecord {
  id: number;
  created_at: string | null;
  date_signed: string | null;
  signature_path: string | null;
  contract_address: AddressSnapshot | AddressSnapshot[] | null;
  customer: {
    id: number;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    phone_number: string | null;
    birthdate: string | null;
    bank_details: string | null;
    gender: number | null;
    address: AddressSnapshot | AddressSnapshot[] | null;
  };
  contract_type: {
    type: string | null;
  };
  worker: {
    full_name: string | null;
    user_id: string | null;
  } | null;
}

interface UtilityDetails {
  previous_provider_name: string | null;
  previous_customer_number: string | null;
  annual_consumption_kwh: number | null;
  meter_point_id: string | null;
  meter_number: string | null;
  meter_reading: number | null;
  unit_price_ct_kwh: number | null;
  base_price_eur_month: number | null;
  delivery_start_date: string | null;
  as_soon_as_possible: boolean | null;
  desired_monthly_payment: number | null;
}

const normalizeRelation = <T>(value: T | T[] | null | undefined): T | null => {
  if (Array.isArray(value)) {
    return value[0] ?? null;
  }
  return value ?? null;
};

const toAddressSnapshot = (
  address: AddressSnapshot | AddressSnapshot[] | null
): AddressSnapshot => {
  const normalized = normalizeRelation(address);
  return {
    id: normalized?.id ?? null,
    street: normalized?.street ?? "",
    house_number: normalized?.house_number ?? "",
    zip_code: normalized?.zip_code ?? "",
    city: normalized?.city ?? "",
    country: normalized?.country ?? "",
  };
};

const formatDateForDisplay = (dateStr: string | null): string => {
  if (!dateStr) {
    return "";
  }

  const datePartMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (datePartMatch) {
    return `${datePartMatch[3]}.${datePartMatch[2]}.${datePartMatch[1]}`;
  }

  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const resolveContractPdfDate = (contract: {
  date_signed: string | null;
  created_at: string | null;
}): string | null => {
  const sourceDate = contract.date_signed ?? contract.created_at;
  if (!sourceDate) {
    return null;
  }

  const formattedDate = formatDateForDisplay(sourceDate);
  return formattedDate || null;
};

const getNumberMetadata = (
  metadata: Record<string, unknown>,
  key: string
): number | null => {
  const value = metadata[key];
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  return null;
};

const toPdfUtility = (
  data: UtilityDetails | null
): ContractPdfData["gasDetails"] => {
  if (!data) {
    return undefined;
  }

  return {
    previous_provider_name: data.previous_provider_name ?? "",
    previous_customer_number: data.previous_customer_number ?? "",
    annual_consumption_kwh:
      data.annual_consumption_kwh !== null
        ? String(data.annual_consumption_kwh)
        : "",
    meter_point_id: data.meter_point_id ?? undefined,
    meter_number: data.meter_number ?? "",
    meter_reading:
      data.meter_reading !== null ? String(data.meter_reading) : undefined,
    unit_price_ct_kwh:
      data.unit_price_ct_kwh !== null ? String(data.unit_price_ct_kwh) : "",
    base_price_eur_month:
      data.base_price_eur_month !== null
        ? String(data.base_price_eur_month)
        : "",
    delivery_start_date: data.delivery_start_date,
    as_soon_as_possible: data.as_soon_as_possible ?? undefined,
    desired_monthly_payment:
      data.desired_monthly_payment !== null
        ? String(data.desired_monthly_payment)
        : undefined,
  };
};

const getObjectMetadata = (value: unknown): Record<string, unknown> =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};

const getRecipientType = (
  row: EmailMessageRow,
  metadata: Record<string, unknown>
): "management" | "customer" => {
  if (metadata["recipientType"] === "management") {
    return "management";
  }
  if (metadata["recipientType"] === "customer") {
    return "customer";
  }
  return row.recipient_email.toLowerCase() === MANAGEMENT_EMAIL
    ? "management"
    : "customer";
};

const loadCustomerSignature = async (
  signaturePath: string | null
): Promise<string | undefined> => {
  if (!signaturePath) {
    return undefined;
  }

  if (signaturePath.startsWith("data:")) {
    return signaturePath;
  }

  try {
    const { data: signatureBlob, error } = await supabase.storage
      .from("contract-signatures")
      .download(signaturePath);

    if (error) {
      throw error;
    }

    if (signatureBlob) {
      const base64 = await blobToBase64(signatureBlob);
      return `data:image/png;base64,${base64}`;
    }
  } catch (error) {
    console.error("Error downloading signature for email retry:", error);
  }

  try {
    return await downloadSignatureAsBase64(signaturePath);
  } catch (error) {
    console.error("Signature fallback failed for email retry:", error);
    return undefined;
  }
};

const loadUtilityDetails = async (
  contractId: number,
  tableName: typeof TABLES.ELECTRICITY_DETAILS | typeof TABLES.GAS_DETAILS
): Promise<UtilityDetails | null> => {
  const { data, error } = await supabase
    .from(tableName)
    .select("*")
    .eq("contract_id", contractId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return data as UtilityDetails | null;
};

export async function retryFailedContractEmail(
  row: EmailMessageRow
): Promise<{ success: boolean; error?: string }> {
  if (
    row.status !== "failed" ||
    row.source !== "contract_api" ||
    row.contract_id === null
  ) {
    return {
      success: false,
      error: "Only failed contract email audit rows can be retried.",
    };
  }

  const { data, error } = await supabase
    .from(TABLES.CONTRACTS)
    .select(
      `
        id,
        created_at,
        date_signed,
        signature_path,
        contract_address:address_id (
          id,
          street,
          house_number,
          zip_code,
          city,
          country
        ),
        customer:customer_id!inner (
          id,
          first_name,
          last_name,
          email,
          phone_number,
          birthdate,
          bank_details,
          gender,
          address:address_id (
            id,
            street,
            house_number,
            zip_code,
            city,
            country
          )
        ),
        contract_type:contract_type_id!inner (
          type
        ),
        worker:worker_id (
          full_name,
          user_id
        )
      `
    )
    .eq("id", row.contract_id)
    .single();

  if (error || !data) {
    console.error("Failed to load contract for email retry:", error);
    return {
      success: false,
      error: "Contract data could not be loaded for retry.",
    };
  }

  const contract = data as unknown as ContractRetryRecord;
  const metadata = getObjectMetadata(row.metadata);
  const metadataCustomerId = getNumberMetadata(metadata, "customerId");
  const expectedCustomerId = row.customer_id ?? metadataCustomerId;
  if (
    expectedCustomerId !== null &&
    expectedCustomerId !== contract.customer.id
  ) {
    return {
      success: false,
      error: "Contract customer does not match the email audit row.",
    };
  }

  const contractType = contract.contract_type.type ?? row.contract_type ?? "";
  if (!contractType) {
    return {
      success: false,
      error: "Contract type is missing for retry.",
    };
  }

  const contractAddress = toAddressSnapshot(contract.contract_address);
  const customerAddress = toAddressSnapshot(contract.customer.address);
  const displayAddress = contractAddress.id ? contractAddress : customerAddress;
  const { templatePath, templateType } = determinePdfTemplate(contractType);
  const electricityDetails =
    templateType === "AUFTRAG_STROM"
      ? await loadUtilityDetails(contract.id, TABLES.ELECTRICITY_DETAILS)
      : null;
  const gasDetails =
    templateType === "AUFTRAG_GAS"
      ? await loadUtilityDetails(contract.id, TABLES.GAS_DETAILS)
      : null;

  let employeeSignatureBase64: string | undefined;
  let workerOrganization: string | undefined;
  let salesEmployeeName = contract.worker?.full_name ?? undefined;
  if (contract.worker?.user_id) {
    const workerData = await getWorkerData(contract.worker.user_id);
    employeeSignatureBase64 = workerData?.signature_base64 ?? undefined;
    workerOrganization = workerData?.organization ?? undefined;
    salesEmployeeName = salesEmployeeName ?? workerData?.full_name ?? undefined;
  }

  const customerSignatureBase64 = await loadCustomerSignature(
    contract.signature_path
  );
  const firstName = contract.customer.first_name ?? "";
  const lastName = contract.customer.last_name ?? "";
  const customerName = `${firstName} ${lastName}`.trim();
  const selectedDate = resolveContractPdfDate(contract);
  if (!selectedDate) {
    return {
      success: false,
      error: "Contract date could not be resolved for PDF regeneration.",
    };
  }

  const retryMetadata: EmailAuditMetadata = {
    source: "contract_api",
    contractId: row.contract_id,
    customerId: row.customer_id ?? contract.customer.id,
    contractType,
    createdByWorkerId: row.created_by_worker_id ?? undefined,
    createdByUserId: row.created_by_user_id ?? undefined,
    recipientType: getRecipientType(row, metadata),
    retryEmailMessageId: row.id,
    retryRequestedAt: new Date().toISOString(),
  };

  const pdfData: ContractPdfData = {
    customer: {
      firstName,
      lastName,
      birthdate: formatDateForDisplay(contract.customer.birthdate),
      email: contract.customer.email ?? "",
      phone: contract.customer.phone_number ?? "",
      address: {
        street: displayAddress.street,
        houseNumber: displayAddress.house_number,
        zipCode: displayAddress.zip_code,
        city: displayAddress.city,
        country: displayAddress.country,
      },
    },
    gender: contract.customer.gender,
    bankDetails: contract.customer.bank_details ?? "",
    signatureBase64: customerSignatureBase64,
    contractType,
    selectedDate,
    employeeSignatureBase64,
    salesEmployeeName,
    salesPartnerCompany: workerOrganization,
    signatureLocation: displayAddress.city,
    electricityDetails:
      templateType === "AUFTRAG_STROM"
        ? toPdfUtility(electricityDetails)
        : undefined,
    gasDetails:
      templateType === "AUFTRAG_GAS" ? toPdfUtility(gasDetails) : undefined,
  };

  const pdfResult = await generateContractPdf(pdfData, templatePath);
  if (!pdfResult.success || !pdfResult.pdfBlob) {
    return {
      success: false,
      error: pdfResult.error ?? "PDF generation failed for retry.",
    };
  }

  const sendResult = await retryContractEmail(
    pdfResult.pdfBlob,
    row.recipient_email,
    customerName || row.recipient_email,
    contractType,
    retryMetadata
  );

  return {
    success: sendResult.success,
    error: sendResult.error,
  };
}
